/**
 * Horizontally scrollable ISO-week selector for the task board.
 *
 * Renders, left to right: past weeks (grayed), then a "Kaikki" chip (clears
 * the deadline filter) and an "Ei määräaikaa" chip (no due date), then the
 * current week and future weeks. Week chips cover those that contain matching
 * tasks — plus the current week even when empty. One selection at a time. The
 * current week is marked by its "Kuluva vk" badge (and the mount
 * auto-centering), with the two control chips sitting immediately to its left.
 *
 * Chips are uniform-height, rectangular, gray when unselected and blue when
 * selected — matching the kanban column header look. A week chip leads with a
 * small badge holding the signed offset from the current week (e.g. "+2"), or
 * "Kuluva vk" for the current week, followed by "vk N, d.m." (week number and
 * the week's Monday). All badges share the same white-on-normal treatment;
 * blue is reserved for the selected chip. Overdue weeks render as faintly red
 * chips.
 *
 * The row is mouse-scrollable: the wheel pans it horizontally. On desktop
 * (`showScrollButtons`) a control group sits to its left — previous/next chevron
 * buttons (which page the partially-hidden chip fully into view via an
 * accumulating, spam-clickable rAF animator) flanking a "Tänään" button that
 * smoothly recenters the current week. On mobile the row scrolls freely and
 * reaches the screen edges.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ChevronLeftIcon, ChevronRightIcon, DateIcon } from "@/lib/icons";
import type { DeadlineWeekValue } from "@/stores/useTaskFilterStore";
import type { WeekBucket } from "@/hooks/useTaskWeekBuckets";
import {
  compareIsoWeeks,
  formatWeekChipLabel,
  formatWeekMonday,
  getCurrentIsoWeek,
  isoWeekEquals,
  isoWeekOffset,
} from "@/lib/weekUtils";

type Props = {
  value: DeadlineWeekValue;
  onChange: (next: DeadlineWeekValue) => void;
  buckets: WeekBucket[]; // sorted chronologically by parent
  totalCount: number; // for the "Kaikki" chip
  noDeadlineCount: number; // for the "Ei määräaikaa" chip
  loading?: boolean;
  showScrollButtons?: boolean; // desktop only: flank the row with chevrons
};

function SelectorChip({
  selected,
  hasOverdue,
  isPast,
  onClick,
  label,
  badge,
  chipRef,
}: {
  selected: boolean;
  hasOverdue?: boolean;
  isPast?: boolean;
  onClick: () => void;
  label: string;
  badge?: string; // small leading pill, e.g. the signed week offset
  chipRef?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={chipRef}
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "relative flex h-9 shrink-0 items-center gap-1.5 rounded-md px-3 text-xs transition-colors duration-150",
        "active:scale-[0.97]",
        // Precedence: selected (blue) → overdue (faint red) → normal. The
        // current week shares the normal background — it's marked by its
        // "Tänään" badge and the mount auto-centering, not a chip tint. Past
        // weeks also share the normal background but mute the label text below.
        // Chips are borderless; the background fill carries the state.
        selected
          ? "bg-primary text-primary-foreground shadow-sm"
          : hasOverdue
            ? "bg-destructive/10 text-destructive hover:bg-destructive/20"
            : "bg-muted text-foreground hover:bg-muted/50",
      )}
    >
      {badge != null && (
        <span
          className={cn(
            "inline-flex items-center rounded px-1.5 py-1 text-xs font-semibold leading-none tabular-nums",
            // Borderless pill. Text precedence mirrors the chip: selected →
            // overdue (lighter red) → past (gray) → normal. The current week's
            // badge takes the normal treatment like any offset badge — blue is
            // reserved for selection here, so tinting it would read as selected.
            selected
              ? "bg-primary-foreground/20 text-primary-foreground"
              : hasOverdue
                ? "bg-background text-destructive/70"
                : isPast
                  ? "bg-background text-muted-foreground"
                  : "bg-background text-foreground",
          )}
        >
          {badge}
        </span>
      )}
      <span
        className={cn(
          "font-medium leading-none tabular-nums",
          isPast && !selected && !hasOverdue && "text-muted-foreground",
        )}
      >
        {label}
      </span>
    </button>
  );
}

// Horizontal padding is applied per layout: mobile (`px-3`) keeps a screen-edge
// gutter, desktop (`px-2`) sits close to the chevron dividers.
const SCROLLER_CLASS =
  "flex items-center gap-2 overflow-x-auto py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

export function WeekSelector({
  value,
  onChange,
  buckets,
  totalCount,
  noDeadlineCount,
  loading,
  showScrollButtons,
}: Props) {
  const currentWeek = useMemo(() => getCurrentIsoWeek(), []);
  const currentChipRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  // Chevron-scroll animation: the accumulating target scroll offset and the
  // running rAF handle (both null when idle). See scrollByDirection.
  const scrollTargetRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  // Merge buckets with the current week (synthetic 0-count entry if absent),
  // then sort chronologically.
  const weekChips = useMemo(() => {
    const list: WeekBucket[] = buckets.map((b) => ({ ...b }));
    if (!list.some((b) => isoWeekEquals(b, currentWeek))) {
      list.push({
        year: currentWeek.year,
        week: currentWeek.week,
        count: 0,
        hasOverdue: false,
      });
    }
    list.sort((a, b) => compareIsoWeeks(a, b));
    return list;
  }, [buckets, currentWeek]);

  // Split the weeks around the current week so the "Kaikki"/"Ei määräaikaa"
  // control chips can sit between the past weeks and the current week.
  const pastWeeks = useMemo(
    () => weekChips.filter((w) => isoWeekOffset(w, currentWeek) < 0),
    [weekChips, currentWeek],
  );
  const currentAndFuture = useMemo(
    () => weekChips.filter((w) => isoWeekOffset(w, currentWeek) >= 0),
    [weekChips, currentWeek],
  );

  // Recompute which scroll affordances apply, given the current scroll offset.
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  // Abort any in-flight chevron-scroll animation. Called when the user takes
  // over scrolling manually (wheel/drag) — otherwise the rAF loop keeps pulling
  // scrollLeft back toward its old target and the row feels stuck.
  const cancelChevronScroll = useCallback(() => {
    scrollTargetRef.current = null;
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  // Auto-center the current-week chip on mount and whenever the chip set
  // changes (other filters may shift which weeks are present, and the
  // current-week chip's DOM node identity changes with it). Scope the scroll to
  // the inner container ref so only it scrolls, then refresh button state.
  useEffect(() => {
    currentChipRef.current?.scrollIntoView({
      inline: "center",
      block: "nearest",
      behavior: "instant" as ScrollBehavior,
    });
    updateScrollState();
  }, [weekChips, updateScrollState]);

  // Track scroll position and container resize to toggle the chevron buttons.
  // `weekChips` is a dependency so this re-attaches once the real scroller
  // mounts after the loading skeleton (where `scrollRef.current` was still
  // null) — otherwise the scroll listener would never bind and the buttons'
  // disabled state would freeze.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    // Translate vertical wheel scrolling into horizontal panning when the
    // wheel's dominant axis is vertical, and prevent the page from scrolling
    // while the pointer is over the row. Must be non-passive to preventDefault.
    // Either way the user is now driving the scroll, so abort any chevron
    // animation still pulling toward an old target.
    const onWheel = (e: WheelEvent) => {
      cancelChevronScroll();
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    // A pointer press on the row (scrollbar drag, touch/trackpad pan) likewise
    // hands control to the user — cancel the chevron animation.
    el.addEventListener("pointerdown", cancelChevronScroll, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("pointerdown", cancelChevronScroll);
      ro.disconnect();
    };
  }, [updateScrollState, weekChips, cancelChevronScroll]);

  // Cancel any in-flight chevron-scroll animation on unmount.
  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Single shared easing loop toward scrollTargetRef. Runs until the offset is
  // within ~0.5px of the target, then snaps and stops. A live target lets rapid
  // chevron clicks accumulate without restarting a slow per-click animation.
  const animateScroll = useCallback(() => {
    const el = scrollRef.current;
    const target = scrollTargetRef.current;
    if (!el || target == null) {
      rafRef.current = null;
      return;
    }
    if (Math.abs(target - el.scrollLeft) < 0.5) {
      el.scrollLeft = target;
      scrollTargetRef.current = null;
      rafRef.current = null;
      return;
    }
    el.scrollLeft += (target - el.scrollLeft) * 0.25;
    rafRef.current = requestAnimationFrame(animateScroll);
  }, []);

  // Page toward an end, bringing the first partially-hidden chip fully into
  // view (with a small gutter). Uses client rects so padding/offsetParent quirks
  // don't skew the math. Each click adds a chip-step to the pending target
  // (clamped to the scrollable range) so spam-clicks accumulate; offsetting
  // every child edge by the as-yet-unscrolled `pending` distance keeps the
  // alignment chip-accurate relative to where the animation will end.
  const scrollByDirection = useCallback(
    (dir: "left" | "right") => {
      const el = scrollRef.current;
      if (!el) return;
      const GUTTER = 8;
      // Seed the target to the live position only when idle, so an in-flight
      // animation keeps accumulating from its existing target.
      if (scrollTargetRef.current == null) scrollTargetRef.current = el.scrollLeft;
      const target = scrollTargetRef.current;
      const pending = target - el.scrollLeft;
      const containerRect = el.getBoundingClientRect();
      const children = Array.from(el.children) as HTMLElement[];
      let delta: number;
      if (dir === "right") {
        // First chip whose projected right edge extends past the container's
        // right edge: align it just inside so it becomes fully visible.
        const next = children.find(
          (c) =>
            c.getBoundingClientRect().right - pending > containerRect.right + 1,
        );
        delta = next
          ? next.getBoundingClientRect().right -
            pending -
            containerRect.right +
            GUTTER
          : el.clientWidth; // nothing peeking — page forward as a fallback
      } else {
        // Last chip whose projected left edge extends past the container's left
        // edge: align it just inside so it becomes fully visible.
        const prev = [...children]
          .reverse()
          .find(
            (c) =>
              c.getBoundingClientRect().left - pending < containerRect.left - 1,
          );
        delta = prev
          ? prev.getBoundingClientRect().left -
            pending -
            containerRect.left -
            GUTTER
          : -el.clientWidth; // nothing peeking — page back as a fallback
      }
      const max = el.scrollWidth - el.clientWidth;
      scrollTargetRef.current = Math.max(0, Math.min(max, target + delta));
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(animateScroll);
      }
    },
    [animateScroll],
  );

  // Recenter the current-week chip. Smooth `scrollIntoView` is unreliable on a
  // nested horizontal scroller (Chromium frequently no-ops it), so instead drive
  // the shared rAF easing loop — the same path the chevrons use. Compute the
  // centered target from client rects (the chip's offsetParent isn't the
  // scroller, so offsetLeft can't be used) and clamp to the scrollable range.
  const jumpToToday = useCallback(() => {
    cancelChevronScroll();
    const el = scrollRef.current;
    const chip = currentChipRef.current;
    if (!el || !chip) return;
    const containerRect = el.getBoundingClientRect();
    const chipRect = chip.getBoundingClientRect();
    const delta =
      chipRect.left +
      chipRect.width / 2 -
      (containerRect.left + containerRect.width / 2);
    const max = el.scrollWidth - el.clientWidth;
    scrollTargetRef.current = Math.max(0, Math.min(max, el.scrollLeft + delta));
    if (rafRef.current == null) {
      rafRef.current = requestAnimationFrame(animateScroll);
    }
  }, [cancelChevronScroll, animateScroll]);

  // Loading placeholder: keep the layout from jumping before buckets arrive.
  if (loading && buckets.length === 0) {
    return (
      <div className={cn(SCROLLER_CLASS, "px-3")}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-9 w-20 shrink-0 animate-pulse rounded-md bg-muted"
          />
        ))}
      </div>
    );
  }

  const renderWeekChip = (w: WeekBucket) => {
    const isCurrent = isoWeekEquals(w, currentWeek);
    const isSelected =
      value.kind === "week" && value.year === w.year && value.week === w.week;
    const offset = isoWeekOffset(w, currentWeek);
    const badge = isCurrent
      ? "Kuluva vk"
      : offset > 0
        ? `+${offset}`
        : `${offset}`;
    return (
      <SelectorChip
        key={`${w.year}-${w.week}`}
        chipRef={isCurrent ? currentChipRef : undefined}
        selected={isSelected}
        hasOverdue={w.hasOverdue}
        isPast={offset < 0}
        onClick={() => onChange({ kind: "week", year: w.year, week: w.week })}
        badge={badge}
        label={`${formatWeekChipLabel(w)}, ${formatWeekMonday(w)}`}
      />
    );
  };

  const chips = (
    <>
      {pastWeeks.map(renderWeekChip)}
      <SelectorChip
        selected={value.kind === "noDeadline"}
        onClick={() => onChange({ kind: "noDeadline" })}
        label="Ei määräaikaa"
      />
      <SelectorChip
        selected={value.kind === "all"}
        onClick={() => onChange({ kind: "all" })}
        label="Kaikki"
      />
      {currentAndFuture.map(renderWeekChip)}
    </>
  );

  if (showScrollButtons) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            disabled={!canScrollLeft}
            onClick={() => scrollByDirection("left")}
            aria-label="Vieritä vasemmalle"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 shrink-0 gap-1.5 px-3"
            onClick={jumpToToday}
            aria-label="Siirry tähän päivään"
          >
            <DateIcon className="h-4 w-4" />
            Tänään
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            disabled={!canScrollRight}
            onClick={() => scrollByDirection("right")}
            aria-label="Vieritä oikealle"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        </div>
        <div
          ref={scrollRef}
          className={cn(SCROLLER_CLASS, "flex-1 border-x border-border px-3")}
        >
          {chips}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className={cn(SCROLLER_CLASS, "px-3")}
    >
      {chips}
    </div>
  );
}
